import { hexToRgb, getLuminance, safe } from '../utils/contrast.js';
import { getIcon } from '../utils/icons-library.js';

/**
 * TRUST — Sellos de confianza / garantías.
 *
 * Franja de "trust badges" para reforzar la conversión: pago seguro, envío gratis,
 * devolución fácil, garantía, soporte… Cada sello es un icono Lucide (SVG inline,
 * hereda el color de acento) + un título corto + una descripción opcional.
 *
 * Todo el contenido es del cliente/IA (nada hardcodeado en el render): el título de
 * cada sello es editable inline (data-field), y el icono se cambia con el icon-picker
 * (escribe props.badges[i].icon en el store). El extractor preserva el icono al no
 * estar en `fields` (mismo patrón que services/process).
 *
 * Modelo de datos (props):
 *   { title?, subtitle?, badges:[{ icon, title, desc? }] }
 *
 * Variantes (CSS-driven, clases `.trust-*` compartidas → DRY):
 *   v1 = Franja: sellos en línea, separados por hairline, icono a la izquierda.
 *   v2 = Tarjetas: grid de tarjetas con icono centrado arriba.
 */
export const trustBlock = (rawProps, index = 0) => {
    const p = safe(rawProps);
    const variant = p.variant || 'v1';
    // safe() normaliza p.items/plans/list, no p.badges → default aquí.
    const badges = Array.isArray(p.badges) ? p.badges : [];

    // Contraste de los botones de la toolbar (idéntico a services/process/features).
    const isDark = getLuminance(...hexToRgb(p.bgColor)) < 0.179;
    const btnBg = isDark ? '#ffffff' : '#111827';
    const btnIcon = isDark ? '#111827' : '#ffffff';

    // Toolbar por sello (reusa handlers globales: add/borrar/drag/icon-picker).
    const itemToolbar = (i) => `
        ${i !== 0 ? `<button class="item-add-btn card-add-left" data-array="badges" data-index="${i}">+</button>` : ''}
        <button class="item-add-btn card-add-right" data-array="badges" data-index="${i}">+</button>
        <div class="item-tools">
            <button class="tool-btn icon-picker-btn" data-array="badges" data-index="${i}" title="Cambiar icono" style="background: ${btnBg} !important; color: ${btnIcon} !important;">${window.icons.palette || '🎨'}</button>
            <button class="tool-btn item-drag-handle" title="Mover" style="background: ${btnBg} !important; color: ${btnIcon} !important;">${window.icons.drag}</button>
            <button class="tool-btn item-delete-btn" data-array="badges" data-index="${i}" title="Borrar" style="background: ${btnBg} !important; color: ${btnIcon} !important;">${window.icons.trash}</button>
        </div>`;

    // Icono del sello (fallback 'shield-check' — el más representativo de "garantía").
    const badgeIcon = (b, size = 26) => getIcon(b.icon || 'shield-check', size);

    // Encabezado opcional (una franja de sellos suele ir sin título; se pinta solo si existe).
    const header = (p.title || p.subtitle) ? `
        <div class="trust-head">
            ${p.title ? `<h2 data-field="title" contenteditable="true">${p.title}</h2>` : ''}
            ${p.subtitle ? `<p data-field="subtitle" contenteditable="true">${p.subtitle}</p>` : ''}
        </div>` : '';

    const cardAttrs = (i) => `data-item-index="${i}" data-array-name="badges" data-array="badges" data-index="${i}" style="--stagger-i:${i};"`;

    const badgeInner = (b, i, size) => `
        ${itemToolbar(i)}
        <span class="trust-icon">${badgeIcon(b, size)}</span>
        <div class="trust-text">
            <h3 class="trust-title" data-field="title" contenteditable="true">${b.title || ''}</h3>
            ${b.desc !== undefined ? `<p class="trust-desc" data-field="desc" contenteditable="true">${b.desc || ''}</p>` : `<p class="trust-desc" data-field="desc" contenteditable="true"></p>`}
        </div>`;

    const baseStyle = `--bg:${p.bgColor}; --text:${p.textColor}; --accent:${p.accentColor}; --accent-contrast:${p.accentContrast}; --font:'${p.fontFamily || "Inter"}'; padding: 64px 8%;`;

    // ═══════════════════════════════════════════
    // v2 — Tarjetas: grid de cards con icono centrado
    // ═══════════════════════════════════════════
    if (variant === 'v2') {
        return `
    <section id="bloque-trust-${index}" class="reveal variant-${variant} trust-section" style="${baseStyle}">
        ${header}
        <div class="trust-grid">
            ${badges.map((b, i) => `
            <div class="trust-badge trust-card sortable-item stagger-item" ${cardAttrs(i)}>
                ${badgeInner(b, i, 30)}
            </div>`).join('')}
        </div>
    </section>`;
    }

    // ═══════════════════════════════════════════
    // v1 (default) — Franja: sellos en línea separados por hairline
    // ═══════════════════════════════════════════
    return `
    <section id="bloque-trust-${index}" class="reveal variant-${variant} trust-section" style="${baseStyle}">
        ${header}
        <div class="trust-strip">
            ${badges.map((b, i) => `
            <div class="trust-badge trust-row sortable-item stagger-item" ${cardAttrs(i)}>
                ${badgeInner(b, i, 26)}
            </div>`).join('')}
        </div>
    </section>`;
};
